import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, RefreshCw, Crosshair } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { formatArrow, arrowColor } from "../components/ScorePad";
import { resolveAvatar } from "../lib/avatars";
import { TargetViewer } from "../components/TargetFace";

function endTotal(end) {
  return (end || []).reduce((s, v) => s + (v || 0), 0);
}
function serieTotal(serie) {
  return (serie || []).reduce((s, end) => s + endTotal(end), 0);
}
function countX(serie, isXSerie) {
  let c = 0;
  (serie || []).forEach((end, ei) => end.forEach((v, ai) => { if (isXSerie?.[ei]?.[ai]) c++; }));
  return c;
}
function countVal(serie, val) {
  let c = 0;
  (serie || []).forEach((end) => end.forEach((v) => { if (v === val) c++; }));
  return c;
}

function SerieTable({ serie, isXSerie, label }) {
  const { t } = useI18n();
  if (!serie || !serie.length) return null;
  const total = serieTotal(serie);
  const xs = countX(serie, isXSerie);
  const tens = countVal(serie, 10);
  const nines = countVal(serie, 9);
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-md overflow-hidden">
      {label && (
        <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">{label}</span>
          <span className="font-heading font-black text-xl text-cyan-400 tabular-nums">{total}</span>
        </div>
      )}
      <div className="overflow-x-auto p-4 md:p-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 uppercase text-xs tracking-[0.2em]">
              <th className="text-left pb-3 pr-3">{t("misc.end_word")}</th>
              {serie[0].map((_, i) => <th key={i} className="pb-3 px-1">F{i + 1}</th>)}
              <th className="pb-3 px-2">S</th>
            </tr>
          </thead>
          <tbody>
            {serie.map((end, ei) => (
              <tr key={ei} className="border-t border-zinc-800">
                <td className="py-2 pr-3 text-zinc-400 font-heading font-bold">{ei + 1}</td>
                {end.map((v, ai) => (
                  <td key={ai} className="py-2 px-1">
                    <div className={`h-10 w-10 md:h-11 md:w-11 rounded-md flex items-center justify-center font-heading font-black ${arrowColor(v, isXSerie?.[ei]?.[ai])}`}>
                      {formatArrow(v, isXSerie?.[ei]?.[ai])}
                    </div>
                  </td>
                ))}
                <td className="py-2 px-2 font-heading font-black text-cyan-400 text-lg tabular-nums">{endTotal(end)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Stats X / 10 / 9 */}
      <div className="border-t border-zinc-800 px-4 py-3 flex gap-6 text-sm">
        <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">X</span><span className="font-heading font-bold text-yellow-400">{xs}</span></div>
        <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">10</span><span className="font-heading font-bold text-white">{tens}</span></div>
        <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">9</span><span className="font-heading font-bold text-white">{nines}</span></div>
        <div className="ml-auto font-heading font-black text-xl text-cyan-400">{total} pts</div>
      </div>
    </div>
  );
}

export default function SoloSpectator() {
  const { t } = useI18n();
  const { code } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setError(null);
      const { data } = await api.get(`/solo/spectate/${code}`);
      setS(data);
    } catch (e) {
      setError("Session introuvable ou code invalide.");
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => { load(); }, 5000);
    return () => clearInterval(interval);
  }, [code]); // eslint-disable-line

  const series = useMemo(() => {
    if (!s) return [];
    const raw = s.scores || [];
    if (!raw.length) return [];
    if (Array.isArray(raw[0]?.[0])) return raw;
    return [raw];
  }, [s]);

  const isXSeries = useMemo(() => {
    if (!s) return [];
    const raw = s.is_x || [];
    if (!raw.length) return [];
    if (Array.isArray(raw[0]?.[0])) return raw;
    return [raw];
  }, [s]);

  const grandTotal = useMemo(() => series.reduce((t, serie) => t + serieTotal(serie), 0), [series]);
  const grandX = useMemo(() => series.reduce((t, serie, si) => t + countX(serie, isXSeries[si]), 0), [series, isXSeries]);
  const grand10 = useMemo(() => series.reduce((t, serie) => t + countVal(serie, 10), 0), [series]);
  const grand9 = useMemo(() => series.reduce((t, serie) => t + countVal(serie, 9), 0), [series]);
  const [showViewer, setShowViewer] = useState(false);
  const hasCoords = useMemo(() => {
    const found = (x) => {
      if (Array.isArray(x)) return x.some(found);
      if (x && typeof x === "object" && "nx" in x) return x.nx !== 0.5 || x.ny !== 0.5;
      return false;
    };
    return found(s?.coords);
  }, [s]);

  if (error) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4">
      <div className="text-red-400 uppercase tracking-widest text-sm">{error}</div>
      <button onClick={() => navigate("/dashboard")} className="text-zinc-500 hover:text-white text-xs uppercase tracking-[0.2em]">
        Retour au tableau de bord
      </button>
    </div>
  );

  if (!s) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
      <div className="text-zinc-500 uppercase tracking-widest">{t("common.loading")}</div>
    </div>
  );

  const player = s.player;
  const avatarSrc = resolveAvatar(player);

  return (
    <>
      {showViewer && (
        <TargetViewer
          withX={s.settings?.with_x !== false}
          scores={s.scores}
          isXData={s.is_x}
          coords={s.coords}
          onClose={() => setShowViewer(false)}
        />
      )}
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      <header className="sticky top-0 z-20 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-zinc-900 rounded-md">
              <ArrowLeft className="h-4 w-4" />
            </button>
            {/* Avatar + pseudo */}
            {player && (
              <div className="flex items-center gap-2">
                <img src={avatarSrc} alt={player.nickname} className="h-8 w-8 rounded-full object-cover border border-zinc-700" />
                <div>
                  <div className="font-heading font-black text-sm uppercase tracking-tight leading-none">{player.nickname}</div>
                  <div className="flex items-center gap-1 text-xs text-cyan-400 mt-0.5">
                    <Eye className="h-3 w-3" /> Spectateur
                  </div>
                </div>
              </div>
            )}
            {!player && (
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-cyan-400">
                <Eye className="h-3 w-3" /> Mode Spectateur
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded ${s.status === "finished" ? "bg-zinc-800 text-zinc-400" : "bg-green-400/10 text-green-400"}`}>
              {s.status === "finished" ? t("misc.finished") : t("misc.in_progress")}
            </div>
            <button onClick={refresh} disabled={refreshing} className="p-2 hover:bg-zinc-900 rounded-md text-zinc-400 hover:text-white transition-colors">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("misc.total")}</div>
              <div className="font-heading font-black text-3xl text-cyan-400 tabular-nums">{grandTotal}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Carte joueur */}
        {player && (
          <div className="flex items-center gap-4 px-4 py-4 bg-zinc-900/50 border border-zinc-800 rounded-md">
            <img src={avatarSrc} alt={player.nickname} className="h-14 w-14 rounded-full object-cover border-2 border-zinc-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-black text-xl uppercase tracking-tight">{player.nickname}</div>
              {s.settings?.session_name && (
                <div className="text-sm text-zinc-400 mt-0.5">{s.settings.session_name}</div>
              )}
              <div className="text-xs text-zinc-500 mt-1">
                {s.settings?.ends} volées · {s.settings?.arrows_per_end} flèches
                {s.settings?.series > 1 && ` · ${s.settings.series} séries`}
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading font-black text-4xl text-cyan-400 tabular-nums">{grandTotal}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mt-0.5">pts</div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 px-4 py-3 bg-cyan-400/5 border border-cyan-400/20 rounded-md text-sm text-cyan-300">
          <Eye className="h-4 w-4 shrink-0" />
          <span>{t("misc.auto_refresh")}</span>
        </div>

        {hasCoords && (
          <button onClick={() => setShowViewer(true)}
            className="w-full inline-flex items-center justify-center gap-2 bg-cyan-400/10 hover:bg-cyan-400/20 border-2 border-cyan-400/60 hover:border-cyan-400 text-cyan-400 font-bold uppercase tracking-wide px-6 py-3 rounded-md transition-all">
            <Crosshair className="h-4 w-4" /> {t("misc.view_impacts")}
          </button>
        )}

        {series.map((serie, si) => (
          <SerieTable
            key={si}
            serie={serie}
            isXSerie={isXSeries[si]}
            label={series.length > 1 ? `${t("misc.series")} ${si + 1}` : null}
          />
        ))}

        {/* Total général si plusieurs séries */}
        {series.length > 1 && (
          <div className="bg-zinc-900/80 border border-cyan-400/30 rounded-md px-4 py-4">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("misc.total_of")} {series.length} {t("misc.series_plural")}</div>
            <div className="flex items-center gap-6">
              <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">X</span><span className="font-heading font-bold text-yellow-400">{grandX}</span></div>
              <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">10</span><span className="font-heading font-bold text-white">{grand10}</span></div>
              <div><span className="text-zinc-500 text-xs uppercase tracking-[0.15em] mr-1">9</span><span className="font-heading font-bold text-white">{grand9}</span></div>
              <div className="ml-auto font-heading font-black text-3xl text-cyan-400 tabular-nums">{grandTotal} pts</div>
            </div>
          </div>
        )}

        {s.status === "finished" && (
          <div className="bg-zinc-900/50 border border-cyan-400/30 rounded-md p-6 text-center">
            <div className="text-cyan-400 text-xs uppercase tracking-[0.3em]">{t("misc.session_finished")}</div>
            <div className="mt-2 font-heading font-black text-3xl uppercase">{t("misc.final_score")} : {grandTotal}</div>
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/50 border border-zinc-800 rounded-md">
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("misc.spectator_code")}</span>
          <span className="font-heading font-black text-xl tracking-[0.3em] text-white">{code}</span>
        </div>
      </main>
    </div>
    </>
  );
}